import { HttpBase } from "@library/Security";
import type { HttpResult } from '@library/Security';
import type { ICompleteWelcomeFlowRequest } from "../../../Carbucks/Types/ICompleteWelcomeFlowRequest";
import type { IUpsertVehicleRequest, IWelcomeFlowData } from "./WelcomeFlowApiRequests";
import type { IWelcomeFlowVehicle } from "../WelcomeFlow.types";

export const useWelcomeFlowApi = () => new class WelcomeFlowApi extends HttpBase
{
    /**
     * Attempts to find the customers vehicle using its registration.  Only checks against incomplete
     * user profiles.
     * @param rego
     * @returns
     */
    async getVehicleDetailsAsync( consumerId: string, rego: string, regoState: string ) : Promise<HttpResult<IWelcomeFlowVehicle>>
    {
        const result = await this.get( `welcome-flow/vehicle/details?consumerId=${consumerId}&rego=${rego}&regoState=${regoState}`);

        return result;
    }
    
    /**
     * Gets the consumer details
     * @param consumerId
     * @returns
     */
    async getConsumerDetailsAsync( consumerId: string, vehicleId: string )
    {
        const result = await this.get( `welcome-flow/consumer/details?consumerId=${consumerId}&vehicleId=${vehicleId}` );

        return result;
    }

    /**
     * Completes the welcome flow, using the vehicle details model
     * @param model
     * @returns
     */
    async completeWelcomeFlowAsync( model: ICompleteWelcomeFlowRequest )
    {
        const result = await this.post( 'welcome-flow/complete', model );

        return result;
    }

    /**
     * Creates a new vehicle or updates an existing one based on the request
     * @param request The vehicle details to create/update
     * @returns the vehicle id (either passed in or the new one created) as well as a flag indicating if the consumer needs to select an app
     */
    async claimVehicleAsync( request: IUpsertVehicleRequest )
    {
        const result = await this.post( 'welcome-flow/vehicle/claim', request );

        return result;
    }

    /**
     * Updates the consumer and businessConsumer details
     * @param consumerId
     * @param firstName
     * @param lastName
     * @returns
     */
    async updateConsumerDetailsAsync( consumerId: string, vehicleId: string, firstName: string, lastName: string )
    {
        const result = await this.put( 'welcome-flow/consumer/update', { consumerId, vehicleId, firstName, lastName } );

        return result;
    }

    /**
     * Sends a mobile verification code to the user
     * @param consumerId
     * @param mobileNumber
     * @returns
     */
    async sendMobileVerificationCodeAsync( consumerId: string, mobileNumber: string )
    {
        const result = await this.post( 'api/consumer/send-mobile-verification-code', { consumerId, mobileNumber } );

        return result;
    }

    /**
     * Verifies the mobile verification code for the user
     * @param consumerId
     * @param code
     * @returns
     */
    async verifyMobileVerificationCodeAsync( consumerId: string, vehicleId: string, code: string )
    {
        const result = await this.post( 'welcome-flow/verify-mobile-verification-code', { consumerId, vehicleId, code } );

        return result;
    }

    /**
     * Submits the originator code for the user
     * @param originatorCode
     * @returns a [] of CarbucksAppTileDto
     * if an app specific code is submitted, the response will be an array of 1 item
     * if a client code is submitted, the response will be an array of all app tiles under that client
     */
    async submitOriginatorCodeAsync( originatorCode: string )
    {
        const result = await this.post( 'welcome-flow/submit-originator-code', { appOriginatorCode: originatorCode } );

        return result;
    }

    /**
     * Selects the app configuration for the user
     * @param appConfigurationId
     * @returns
     */
    async setAppConfigurationAsync( vehicleId: string, appConfigurationId: string )
    {
        const result = await this.post( 'welcome-flow/set-app-configuration', { vehicleId, appConfigurationId } );

        return result;
    }

    /**
     * Updates the mobile number for the consumer
     * @param consumerId
     * @param mobileNumber
     * @returns
     */
    async updateMobileAsync( consumerId: string, mobilePhoneNumber: string )
    {
        const result = await this.put( 'welcome-flow/consumer/update-mobile-phone-number', { consumerId, mobilePhoneNumber } );

        return result;
    }

    /**
     * Verifies the mobile number for the consumer
     * @param consumerId
     * @param mobileNumber
     * @returns
     */
    async verifyMobileAsync( consumerId: string, vehicleId: string, mobilePhoneNumber: string, verificationCode: string )
    {
        const result = await this.post( 'welcome-flow/consumer/verify-mobile-phone-number', { consumerId, vehicleId, mobilePhoneNumber, verificationCode } );

        return result;
    }

    /**
     * Gets the data required for initialising the welcome flow
     * @param vehicleId 
     * @returns 
     * An object containing the last completed step, whether the consumer needs to select an app, and whether the consumer needs to verify their mobile number
     */
    async getWelcomeFlowDataAsync( consumerId: string, vehicleId: string ) : Promise<HttpResult<IWelcomeFlowData>>
    {
        const result = await this.get( `welcome-flow/data?consumerId=${consumerId}&vehicleId=${vehicleId}` );

        return result;
    }
}